import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, FileText, Users, GraduationCap, MessageSquare,
  Settings, UserCog, LogOut, Menu, X, BookOpen, Tag, Star,
  Info, Car, Images, Bell, Play, ChevronDown, ChevronRight,
  Globe, Megaphone,
} from 'lucide-react';
import { useState, useEffect } from 'react';

const GROUPS = [
  {
    key: 'students',
    label: '學員',
    items: [
      { path: '/admin/clients', label: '學員管理', icon: <Users className="h-4 w-4" /> },
    ],
  },
  {
    key: 'content',
    label: '教學內容',
    items: [
      { path: '/admin/instructors', label: '導師', icon: <GraduationCap className="h-4 w-4" /> },
      { path: '/admin/courses-content', label: '課程內容', icon: <Car className="h-4 w-4" /> },
      { path: '/admin/blogs', label: '考試資料', icon: <FileText className="h-4 w-4" /> },
      { path: '/admin/exam-routes', label: '考試攻略', icon: <BookOpen className="h-4 w-4" /> },
      { path: '/admin/testimonials', label: '學員分享', icon: <MessageSquare className="h-4 w-4" /> },
    ],
  },
  {
    key: 'promo',
    label: '推廣',
    items: [
      { path: '/admin/popups', label: '彈出公告', icon: <Bell className="h-4 w-4" /> },
      { path: '/admin/gallery', label: '相片走馬燈', icon: <Play className="h-4 w-4" /> },
    ],
  },
  {
    key: 'website',
    label: '網站',
    items: [
      { path: '/admin/home-features', label: '首頁特色', icon: <Star className="h-4 w-4" /> },
      { path: '/admin/about', label: '關於我們', icon: <Info className="h-4 w-4" /> },
      { path: '/admin/images', label: '圖片管理', icon: <Images className="h-4 w-4" /> },
    ],
  },
  {
    key: 'system',
    label: '設定',
    items: [
      { path: '/admin/settings', label: '網站設定', icon: <Settings className="h-4 w-4" /> },
      { path: '/admin/categories', label: '類別管理', icon: <Tag className="h-4 w-4" /> },
      { path: '/admin/users', label: '帳號管理', icon: <UserCog className="h-4 w-4" /> },
    ],
  },
];

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Remember which groups are open (default: students + content open)
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_sidebar_groups');
      return saved ? JSON.parse(saved) : { students: true, content: true, promo: false, website: false, system: false };
    } catch { return { students: true, content: true, promo: false, website: false, system: false }; }
  });

  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('admin_sidebar_groups', JSON.stringify(next));
      return next;
    });
  };

  // Auto-open group containing active route
  useEffect(() => {
    GROUPS.forEach(group => {
      if (group.items.some(item => location.pathname === item.path)) {
        setOpenGroups(prev => {
          if (prev[group.key]) return prev;
          const next = { ...prev, [group.key]: true };
          localStorage.setItem('admin_sidebar_groups', JSON.stringify(next));
          return next;
        });
      }
    });
  }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/admin/login'); };
  const isActive = (path) => location.pathname === path;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-between p-4 border-b">
        <Link to="/admin/dashboard" className="text-lg font-bold text-primary">智駕駛後台</Link>
        <button onClick={() => setSidebarOpen(false)} className="md:hidden">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {/* Dashboard — always visible */}
        <Link
          to="/admin/dashboard"
          onClick={() => setSidebarOpen(false)}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            isActive('/admin/dashboard') ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <LayoutDashboard className="h-4 w-4" />
          儀表板
        </Link>

        {/* Grouped items */}
        {GROUPS.map(group => {
          const isGroupActive = group.items.some(i => isActive(i.path));
          const isOpen = openGroups[group.key];
          return (
            <div key={group.key}>
              {/* Group header */}
              <button
                onClick={() => toggleGroup(group.key)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-semibold transition-colors mt-1 ${
                  isGroupActive ? 'text-primary bg-primary/5' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span>{group.label}</span>
                {isOpen
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />
                }
              </button>

              {/* Group items */}
              {isOpen && (
                <div className="mt-0.5 ml-2 pl-2 border-l border-gray-200 space-y-0.5">
                  {group.items.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive(item.path)
                          ? 'bg-primary text-white font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t">
        <div className="flex items-center gap-2 px-3 py-2 mb-1">
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
            {user?.username?.[0]?.toUpperCase() || 'A'}
          </div>
          <span className="text-sm font-medium text-gray-700 truncate">{user?.username || 'admin'}</span>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          登出
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-56 bg-white border-r shadow-sm fixed top-0 bottom-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-56 bg-white shadow-xl z-50">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 md:ml-56 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-20 shadow-sm">
          <button onClick={() => setSidebarOpen(true)} className="md:hidden p-1 rounded-md hover:bg-gray-100">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <a href="/" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-100">
            <Globe className="h-3.5 w-3.5" />
            查看網站
          </a>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
